import React from 'react';
import { Box, Typography, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { Phone, Email, LocationOn } from '@mui/icons-material';

const KAKAO_CHANNEL_URL = 'https://pf.kakao.com/_QXtCxj/chat';

const KakaoIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 3C6.477 3 2 6.463 2 10.691c0 2.678 1.782 5.038 4.465 6.385l-.637 3.118a.3.3 0 0 0 .454.324l3.747-2.454c.64.094 1.299.145 1.971.145 5.523 0 10-3.463 10-7.518C22 6.463 17.523 3 12 3Z" fill="currentColor" />
  </svg>
);

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      mt: 0,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.3), transparent)',
      },
    }}>
      <Box sx={{
        maxWidth: { xs: '100%', md: '50%' },
        mx: 'auto',
        px: { xs: 3, md: 0 },
        py: { xs: 3, md: 5 },
      }}>
        {/* 상단: 로고 + 연락처 */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 3, md: 0 },
          mb: { xs: 3, md: 4 },
        }}>
          {/* 로고 + 슬로건 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box
                component="img"
                src="/assets/dwt_logo.png"
                alt="다원트레이드"
                sx={{ height: 28, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
              빠르고 안전한 중고 디지털 기기 매입 전문 기업
            </Typography>
          </Box>

          {/* 빠른 연락처 */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              href="tel:010-2451-6211"
              size="small"
              sx={{
                width: 38, height: 38,
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#94a3b8',
                '&:hover': { bgcolor: 'rgba(59,130,246,0.15)', color: '#60a5fa', borderColor: 'rgba(59,130,246,0.3)' },
                transition: 'all 0.2s',
              }}
            >
              <Phone sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              href="mailto:dawontrade@dwt.kr"
              size="small"
              sx={{
                width: 38, height: 38,
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#94a3b8',
                '&:hover': { bgcolor: 'rgba(59,130,246,0.15)', color: '#60a5fa', borderColor: 'rgba(59,130,246,0.3)' },
                transition: 'all 0.2s',
              }}
            >
              <Email sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              href={KAKAO_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                width: 38, height: 38,
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#94a3b8',
                '&:hover': { bgcolor: 'rgba(254,229,0,0.12)', color: '#FEE500', borderColor: 'rgba(254,229,0,0.3)' },
                transition: 'all 0.2s',
              }}
            >
              <KakaoIcon />
            </IconButton>
          </Box>
        </Box>

        {/* 구분선 */}
        <Box sx={{
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
          mb: { xs: 3, md: 4 },
        }} />

        {/* 중단: 회사 정보 그리드 */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 6 },
          mb: { xs: 3, md: 4 },
        }}>
          {/* 회사 정보 */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', mb: 1.5, textTransform: 'uppercase' }}>
              Company Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                <Box component="span" sx={{ color: '#64748b', fontWeight: 600, mr: 1 }}>상호</Box>
                다원트레이드
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                <Box component="span" sx={{ color: '#64748b', fontWeight: 600, mr: 1 }}>대표</Box>
                이민재, 오성민
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                <Box component="span" sx={{ color: '#64748b', fontWeight: 600, mr: 1 }}>사업자등록번호</Box>
                750-81-00782
              </Typography>
            </Box>
          </Box>

          {/* 연락처 */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', mb: 1.5, textTransform: 'uppercase' }}>
              Contact
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Phone sx={{ fontSize: 14, color: '#475569' }} />
                <Typography component="a" href="tel:010-2451-6211" sx={{
                  fontSize: '0.78rem', color: '#94a3b8', textDecoration: 'none',
                  '&:hover': { color: '#60a5fa' }, transition: 'color 0.2s',
                }}>
                  010-2451-6211
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Email sx={{ fontSize: 14, color: '#475569' }} />
                <Typography component="a" href="mailto:dawontrade@dwt.kr" sx={{
                  fontSize: '0.78rem', color: '#94a3b8', textDecoration: 'none',
                  '&:hover': { color: '#60a5fa' }, transition: 'color 0.2s',
                }}>
                  dawontrade@dwt.kr
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <LocationOn sx={{ fontSize: 14, color: '#475569' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  서울특별시 금천구 가산디지털1로 146 501,502호
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* 바로가기 */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', mb: 1.5, textTransform: 'uppercase' }}>
              Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {[
                { label: '개인정보처리방침', href: '/privacy' },
                { label: '안심거래 인증', href: '/trust' },
                { label: '카카오톡 상담', href: KAKAO_CHANNEL_URL, external: true },
              ].map((link) => (
                <Typography
                  key={link.label}
                  component="a"
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  sx={{
                    fontSize: '0.78rem', color: '#94a3b8', textDecoration: 'none',
                    '&:hover': { color: '#60a5fa' }, transition: 'color 0.2s',
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 하단 구분선 */}
        <Box sx={{
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
          mb: 2.5,
        }} />

        {/* 하단: 카피라이트 */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'center', sm: 'center' },
          gap: 1,
        }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#475569' }}>
            &copy; {new Date().getFullYear()} 다원트레이드. All rights reserved.
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#334155' }}>
            중고 단말 안심거래 인증 사업자
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
